<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  collection, query, where, getDocs, doc, setDoc, getDoc, updateDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import { useD4HStore } from '@/stores/d4h'
import {
  SearcherType, RopeQual, SEARCHER_TYPE_OPTIONS, ROPE_QUAL_OPTIONS,
  STANDARD_MISC_QUALS, MEDICAL_QUAL_OPTIONS,
  type Agency, type AgencyInvite, type AgencyDomainRule, type AgencyJoinRequest, type D4HRosterMatch,
} from '@/types'
import D4HRosterMatchNotice from '@/components/agency/D4HRosterMatchNotice.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const authStore = useAuthStore()
const d4hStore = useD4HStore()

// ── State machine ──────────────────────────────────────────────────────────────
type OnboardingState = 'loading' | 'invite' | 'domain' | 'pending' | 'search' | 'profile'
const state = ref<OnboardingState>('loading')

const resolvedAgencyId = ref('')
const resolvedAgencyName = ref('')
const resolvedPath = ref<'A' | 'B' | 'C' | 'new' | 'guest' | null>(null)

// When arriving via an incident invite (redirect = /incidents/{id}/join?…),
// a user may continue without an agency and operate only within that incident.
const guestIncidentId = computed(() => {
  const redirect = route.query.redirect as string | undefined
  const m = redirect?.match(/\/incidents\/([^/?]+)/)
  return m?.[1] ?? ''
})
const resolvedInviteId = ref<string | null>(null)
const resolvedRequestId = ref<string | null>(null)
const wasRejected = ref(false)

// ── Profile form fields ────────────────────────────────────────────────────────
const firstName = ref('')
const lastName = ref('')
const phone = ref('')
const medicalQual = ref('')
const searcherType = ref<SearcherType | null>(null)
const ropeQual = ref<RopeQual | null>(null)
const miscQuals = ref<string[]>([])

const medicalQualOptions = MEDICAL_QUAL_OPTIONS
const searcherTypeOptions = SEARCHER_TYPE_OPTIONS
const ropeQualOptions = ROPE_QUAL_OPTIONS
// Includes agency custom quals pre-filled from D4H, so they show as ticked boxes
const miscQualOptions = computed(() => [
  ...STANDARD_MISC_QUALS,
  ...miscQuals.value.filter((q) => !STANDARD_MISC_QUALS.includes(q)),
])

// ── D4H roster pre-fill ────────────────────────────────────────────────────────
// If the agency has imported a D4H roster and this person is on it (matched by
// their verified sign-in email), pre-fill the profile. Only empty fields are filled.
const rosterMatch = ref<D4HRosterMatch | null>(null)

async function loadRosterMatch(agencyId: string) {
  rosterMatch.value = null
  let match: D4HRosterMatch | null = null
  try {
    match = await d4hStore.fetchRosterMatch(agencyId)
  } catch {
    // Pre-fill is a convenience. Offline or API errors just leave the form empty.
    return
  }
  // Ignore a late response if the person has since picked a different agency
  if (!match || resolvedAgencyId.value !== agencyId) return
  rosterMatch.value = match
  if (!firstName.value.trim()) firstName.value = match.first
  if (!lastName.value.trim()) lastName.value = match.last
  if (!phone.value.trim()) phone.value = match.phone
  if (!medicalQual.value && match.medical_qual) medicalQual.value = match.medical_qual
  if (searcherType.value === null && match.searcher_type) searcherType.value = match.searcher_type
  if (ropeQual.value === null && match.rope_qual) ropeQual.value = match.rope_qual
  miscQuals.value = [...new Set([...miscQuals.value, ...match.misc_quals])]
}

const canSubmit = computed(() => {
  // Guests only need a name + phone — SAR field quals are collected at check-in.
  if (resolvedPath.value === 'guest') {
    return firstName.value.trim() !== '' && phone.value.trim() !== ''
  }
  return (
    firstName.value.trim() !== '' &&
    phone.value.trim() !== '' &&
    medicalQual.value !== '' &&
    searcherType.value !== null &&
    ropeQual.value !== null
  )
})

// ── Agency search ──────────────────────────────────────────────────────────────
const agencyInput = ref<Agency | string>('')
const agencySuggestions = ref<Agency[]>([])
const agencySearchText = ref('')
const showSuggestions = ref(false)

async function searchAgencies(queryText: string) {
  agencySearchText.value = queryText
  agencyInput.value = queryText
  if (!queryText.trim()) {
    agencySuggestions.value = []
    showSuggestions.value = false
    return
  }
  const q = query(
    collection(db, 'agencies'),
    where('agency_name', '>=', queryText),
    where('agency_name', '<=', queryText + '\uf8ff'),
  )
  const snap = await getDocs(q)
  agencySuggestions.value = snap.docs.map(d => ({ agency_id: d.id, ...d.data() } as Agency))
  showSuggestions.value = agencySuggestions.value.length > 0
}

function selectAgency(agency: Agency) {
  agencyInput.value = agency
  agencySearchText.value = agency.agency_name
  showSuggestions.value = false
}

function handleRequestToJoin() {
  if (typeof agencyInput.value !== 'string' && (agencyInput.value as Agency)?.agency_id) {
    const agency = agencyInput.value as Agency
    resolvedAgencyId.value = agency.agency_id
    resolvedAgencyName.value = agency.agency_name
    resolvedPath.value = 'C'
    state.value = 'profile'
    loadRosterMatch(agency.agency_id)
  }
}

function handleNewAgency() {
  resolvedAgencyName.value = agencySearchText.value.trim()
  resolvedAgencyId.value = ''
  resolvedPath.value = 'new'
  state.value = 'profile'
}

function handleContinueAsGuest() {
  resolvedAgencyId.value = ''
  resolvedAgencyName.value = ''
  resolvedPath.value = 'guest'
  state.value = 'profile'
}

function toggleMiscQual(qual: string) {
  const idx = miscQuals.value.indexOf(qual)
  if (idx === -1) miscQuals.value.push(qual)
  else miscQuals.value.splice(idx, 1)
}

// ── onMounted — resolve which path applies ─────────────────────────────────────
onMounted(async () => {
  state.value = 'loading'
  const user = authStore.currentUser
  if (!user?.email) {
    state.value = 'search'
    return
  }

  const userEmail = user.email.toLowerCase()
  const uid = user.uid
  const now = Timestamp.now()

  // Path A — unclaimed, non-expired invite
  const inviteQ = query(
    collection(db, 'agency_invites'),
    where('email', '==', userEmail),
    where('claimed_by_uid', '==', null),
  )
  const inviteSnap = await getDocs(inviteQ)
  const validInvite = inviteSnap.docs
    .map(d => ({ invite_id: d.id, ...d.data() } as AgencyInvite))
    .find(inv => inv.expires_at.toMillis() > now.toMillis())

  if (validInvite) {
    const agencySnap = await getDoc(doc(db, 'agencies', validInvite.agency_id))
    resolvedAgencyId.value = validInvite.agency_id
    resolvedAgencyName.value = agencySnap.exists() ? (agencySnap.data().agency_name as string) : validInvite.agency_id
    resolvedInviteId.value = validInvite.invite_id
    resolvedPath.value = 'A'
    state.value = 'invite'
    loadRosterMatch(validInvite.agency_id)
    return
  }

  // Path B — domain rule
  const emailDomain = userEmail.split('@')[1]
  if (emailDomain) {
    const domainSnap = await getDoc(doc(db, 'agency_domain_rules', emailDomain))
    if (domainSnap.exists()) {
      const rule = domainSnap.data() as AgencyDomainRule
      const agencySnap = await getDoc(doc(db, 'agencies', rule.agency_id))
      resolvedAgencyId.value = rule.agency_id
      resolvedAgencyName.value = agencySnap.exists() ? (agencySnap.data().agency_name as string) : rule.agency_id
      resolvedPath.value = 'B'
      state.value = 'domain'
      loadRosterMatch(rule.agency_id)
      return
    }
  }

  // Path C — existing pending request
  const pendingQ = query(
    collection(db, 'agency_join_requests'),
    where('user_id', '==', uid),
    where('status', '==', 'pending'),
  )
  const pendingSnap = await getDocs(pendingQ)
  if (!pendingSnap.empty) {
    const doc = pendingSnap.docs[0]!
    const req = { request_id: doc.id, ...doc.data() } as AgencyJoinRequest
    resolvedAgencyId.value = req.agency_id
    resolvedAgencyName.value = req.agency_name
    resolvedRequestId.value = req.request_id
    resolvedPath.value = 'C'
    state.value = 'pending'
    return
  }

  // Previously rejected
  const rejectedQ = query(
    collection(db, 'agency_join_requests'),
    where('user_id', '==', uid),
    where('status', '==', 'rejected'),
  )
  const rejectedSnap = await getDocs(rejectedQ)
  if (!rejectedSnap.empty) {
    wasRejected.value = true
  }

  state.value = 'search'
})

// ── Submit ─────────────────────────────────────────────────────────────────────
async function handleSubmit() {
  try {
    const user = authStore.currentUser
    if (!user) return

    const profileData = {
      user_first_name: firstName.value.trim(),
      user_last_name: lastName.value.trim(),
      phone: phone.value.trim(),
      email: user.email!.toLowerCase(),
      medical_qual: medicalQual.value,
      searcher_type: searcherType.value,
      rope_qual: ropeQual.value,
      misc_quals: miscQuals.value,
    }

    const pending = route.query.redirect as string | undefined

    if (resolvedPath.value === 'guest') {
      // Intentionally omit agency_id — a guest belongs to no agency.
      await authStore.saveUserProfile({
        ...profileData,
        role: 'member',
        guest: true,
        guest_incident_id: guestIncidentId.value || null,
      })
      if (pending) {
        router.push(pending)
      } else if (guestIncidentId.value) {
        router.push({ name: 'incident-overview', params: { id: guestIncidentId.value } })
      } else {
        router.push('/incidents')
      }
      return
    }

    if (resolvedPath.value === 'new') {
      const newRef = doc(collection(db, 'agencies'))
      await setDoc(newRef, {
        agency_name: resolvedAgencyName.value,
        agency_logo: '',
        custom_quals: [],
      })
      await authStore.saveUserProfile({ ...profileData, agency_id: newRef.id, role: 'admin' })
      router.push(pending || '/incidents')
      return
    }

    if (resolvedPath.value === 'A') {
      await updateDoc(doc(db, 'agency_invites', resolvedInviteId.value!), {
        claimed_by_uid: user.uid,
        claimed_at: serverTimestamp(),
      })
      await authStore.saveUserProfile({ ...profileData, agency_id: resolvedAgencyId.value, role: 'member' })
      router.push(pending || '/incidents')
      return
    }

    if (resolvedPath.value === 'B') {
      await authStore.saveUserProfile({ ...profileData, agency_id: resolvedAgencyId.value, role: 'member' })
      router.push(pending || '/incidents')
      return
    }

    if (resolvedPath.value === 'C') {
      const requestRef = doc(collection(db, 'agency_join_requests'))
      const displayName = [firstName.value.trim(), lastName.value.trim()].filter(Boolean).join(' ')
      await setDoc(requestRef, {
        request_id: requestRef.id,
        user_id: user.uid,
        agency_id: resolvedAgencyId.value,
        agency_name: resolvedAgencyName.value,
        user_display_name: displayName,
        user_email: user.email!.toLowerCase(),
        requested_at: serverTimestamp(),
        status: 'pending',
        reviewed_by_uid: null,
        reviewed_at: null,
      })
      // Save profile without agency_id — user stays on onboarding until approved
      await authStore.saveUserProfile(profileData)
      resolvedRequestId.value = requestRef.id
      state.value = 'pending'
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  }
}

async function withdrawRequest() {
  if (!resolvedRequestId.value) return
  try {
    await updateDoc(doc(db, 'agency_join_requests', resolvedRequestId.value), {
      status: 'withdrawn',
    })
    resolvedRequestId.value = null
    resolvedAgencyId.value = ''
    resolvedAgencyName.value = ''
    resolvedPath.value = null
    state.value = 'search'
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  }
}
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="bg-surface px-6 py-4 border-b border-outline-variant/15 shrink-0">
      <span class="text-on-surface text-lg font-semibold tracking-wide uppercase">First on Scene</span>
    </header>
    <div class="flex-1 overflow-y-auto">
      <div class="min-h-full flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <div class="max-w-xl w-full flex flex-col gap-8 bg-surface-container p-6 sm:p-10">

        <!-- Loading -->
        <div v-if="state === 'loading'" class="flex items-center justify-center py-16">
          <span class="w-6 h-6 border-2 border-outline/30 border-t-outline animate-spin rounded-full" />
        </div>

        <!-- Pending approval -->
        <template v-else-if="state === 'pending'">
          <div>
            <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">Request Submitted</h1>
          </div>
          <div class="bg-surface-container-high border border-border rounded p-5 flex flex-col gap-3">
            <p class="text-on-surface font-semibold">Your request to join {{ resolvedAgencyName }} is awaiting admin approval.</p>
            <p class="text-on-surface-variant text-sm">You'll have full access once an admin approves your request. Check back later.</p>
            <FosButton variant="secondary" size="sm" class="self-start" @click="withdrawRequest">
              Withdraw Request
            </FosButton>
          </div>
        </template>

        <!-- Invite (Path A) -->
        <template v-else-if="state === 'invite'">
          <div>
            <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">Complete Your Profile</h1>
            <p class="text-on-surface-variant mt-1 text-sm">This information pre-fills check-in forms during incidents.</p>
          </div>
          <div class="bg-surface-container-high border border-border rounded p-4 flex gap-3 items-start">
            <span class="material-symbols-outlined text-primary text-[20px] mt-0.5 shrink-0">mail</span>
            <div>
              <p class="text-on-surface font-semibold text-sm">You've been invited to join {{ resolvedAgencyName }}</p>
              <p class="text-on-surface-variant text-xs mt-0.5">Complete your profile below to accept the invitation.</p>
            </div>
          </div>
          <D4HRosterMatchNotice v-if="rosterMatch" :match="rosterMatch" />
          <!-- Profile form -->
          <div class="flex flex-col gap-8">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">First Name <span class="text-error">*</span></label>
                <FosInput v-model="firstName" placeholder="First name" />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Last Name</label>
                <FosInput v-model="lastName" placeholder="Last name" />
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Phone <span class="text-error">*</span></label>
              <FosInput v-model="phone" type="tel" placeholder="(555) 000-0000" />
            </div>
            <div class="h-px bg-outline-variant/15" />
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Medical Qualification <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in medicalQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="medicalQual" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Searcher Type <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in searcherTypeOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="searcherType" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Rope Qualification <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in ropeQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="ropeQual" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="h-px bg-outline-variant/15" />
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Other Qualifications</label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <label v-for="opt in miscQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="checkbox" :value="opt" :checked="miscQuals.includes(opt)" class="accent-primary" @change="toggleMiscQual(opt)" />
                  {{ opt }}
                </label>
              </div>
            </div>
          </div>
          <FosButton variant="primary" class="w-full" :disabled="!canSubmit" @click="handleSubmit">
            Accept Invite & Complete Setup
          </FosButton>
        </template>

        <!-- Domain auto-join (Path B) -->
        <template v-else-if="state === 'domain'">
          <div>
            <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">Complete Your Profile</h1>
            <p class="text-on-surface-variant mt-1 text-sm">This information pre-fills check-in forms during incidents.</p>
          </div>
          <div class="bg-surface-container-high border border-border rounded p-4 flex gap-3 items-start">
            <span class="material-symbols-outlined text-primary text-[20px] mt-0.5 shrink-0">domain_verification</span>
            <div>
              <p class="text-on-surface font-semibold text-sm">Your email domain matches {{ resolvedAgencyName }}</p>
              <p class="text-on-surface-variant text-xs mt-0.5">You've been automatically added. Complete your profile to continue.</p>
            </div>
          </div>
          <D4HRosterMatchNotice v-if="rosterMatch" :match="rosterMatch" />
          <!-- Profile form (same as Path A) -->
          <div class="flex flex-col gap-8">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">First Name <span class="text-error">*</span></label>
                <FosInput v-model="firstName" placeholder="First name" />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Last Name</label>
                <FosInput v-model="lastName" placeholder="Last name" />
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Phone <span class="text-error">*</span></label>
              <FosInput v-model="phone" type="tel" placeholder="(555) 000-0000" />
            </div>
            <div class="h-px bg-outline-variant/15" />
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Medical Qualification <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in medicalQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="medicalQual" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Searcher Type <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in searcherTypeOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="searcherType" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Rope Qualification <span class="text-error">*</span></label>
              <div class="flex flex-wrap gap-x-6 gap-y-3">
                <label v-for="opt in ropeQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="radio" :value="opt" v-model="ropeQual" class="accent-primary" />
                  {{ opt }}
                </label>
              </div>
            </div>
            <div class="h-px bg-outline-variant/15" />
            <div class="flex flex-col gap-3">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Other Qualifications</label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <label v-for="opt in miscQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                  <input type="checkbox" :value="opt" :checked="miscQuals.includes(opt)" class="accent-primary" @change="toggleMiscQual(opt)" />
                  {{ opt }}
                </label>
              </div>
            </div>
          </div>
          <FosButton variant="primary" class="w-full" :disabled="!canSubmit" @click="handleSubmit">
            Complete Setup
          </FosButton>
        </template>

        <!-- Agency search (Path C / new agency) -->
        <template v-else-if="state === 'search'">
          <div>
            <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">Join an Agency</h1>
            <p class="text-on-surface-variant mt-1 text-sm">Search for your agency to request access, or create a new one.</p>
          </div>

          <!-- Rejection notice -->
          <div v-if="wasRejected" class="bg-surface-container-high border border-border rounded p-4 flex gap-3 items-start">
            <span class="material-symbols-outlined text-error text-[20px] mt-0.5 shrink-0">cancel</span>
            <div>
              <p class="text-on-surface font-semibold text-sm">Your previous request was not approved.</p>
              <p class="text-on-surface-variant text-xs mt-0.5">Search for a different agency or create your own.</p>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1.5 relative">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Agency <span class="text-error">*</span></label>
              <FosInput
                :model-value="agencySearchText"
                placeholder="Search agency name"
                @update:model-value="searchAgencies($event)"
                @blur="showSuggestions = false"
              />
              <div
                v-if="showSuggestions"
                class="absolute top-full left-0 right-0 z-10 bg-surface-container-high border border-outline-variant/15 mt-0.5"
              >
                <button
                  v-for="suggestion in agencySuggestions"
                  :key="suggestion.agency_id"
                  class="w-full text-left px-3 py-2 text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
                  @mousedown.prevent="selectAgency(suggestion)"
                >
                  {{ suggestion.agency_name }}
                </button>
              </div>
            </div>

            <!-- Existing agency selected -->
            <template v-if="typeof agencyInput !== 'string' && (agencyInput as Agency)?.agency_id">
              <div class="flex items-center justify-between px-3 py-2.5 bg-surface-container-high border border-border rounded">
                <div>
                  <p class="text-sm text-on-surface font-semibold">{{ (agencyInput as Agency).agency_name }}</p>
                  <p class="text-xs text-outline">Submitting a request will notify the agency admin.</p>
                </div>
              </div>
              <FosButton variant="primary" class="w-full" @click="handleRequestToJoin">
                Request to Join
              </FosButton>
            </template>

            <!-- New agency name typed, no results -->
            <template v-else-if="agencySearchText.trim() && agencySuggestions.length === 0 && typeof agencyInput === 'string'">
              <p class="text-xs text-outline">
                No agency found. A new agency named "{{ agencySearchText.trim() }}" will be created and you'll become its admin.
              </p>
              <FosButton variant="secondary" class="w-full" @click="handleNewAgency">
                Create New Agency
              </FosButton>
            </template>
          </div>

          <div class="flex items-center gap-3">
            <div class="h-px bg-outline-variant/15 flex-1" />
            <span class="text-[10px] text-outline uppercase tracking-widest">or</span>
            <div class="h-px bg-outline-variant/15 flex-1" />
          </div>
          <div class="flex flex-col gap-2">
            <p class="text-xs text-outline">
              Invited to help with a single incident? Continue without joining an agency —
              you'll have access to just that incident.
            </p>
            <FosButton variant="tertiary" class="w-full" @click="handleContinueAsGuest">
              Continue without an agency
            </FosButton>
          </div>
        </template>

        <!-- Profile form — Path C or new agency (state = 'profile') -->
        <template v-else-if="state === 'profile'">
          <div>
            <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">Complete Your Profile</h1>
            <p class="text-on-surface-variant mt-1 text-sm">This information pre-fills check-in forms during incidents.</p>
          </div>

          <div class="bg-surface-container-high border border-border rounded p-4 flex gap-3 items-start">
            <span class="material-symbols-outlined text-primary text-[20px] mt-0.5 shrink-0">
              {{ resolvedPath === 'new' ? 'add_business' : resolvedPath === 'guest' ? 'person' : 'group_add' }}
            </span>
            <div>
              <p class="text-on-surface font-semibold text-sm">
                {{ resolvedPath === 'new'
                  ? `Creating new agency "${resolvedAgencyName}"`
                  : resolvedPath === 'guest'
                  ? 'Continuing without an agency'
                  : `Requesting to join ${resolvedAgencyName}` }}
              </p>
              <p class="text-on-surface-variant text-xs mt-0.5">
                {{ resolvedPath === 'new'
                  ? "You'll be set as the agency admin."
                  : resolvedPath === 'guest'
                  ? "You'll have access only to the incident you were invited to."
                  : "Your request will be sent to the agency admin for approval." }}
              </p>
            </div>
          </div>
          <D4HRosterMatchNotice v-if="rosterMatch" :match="rosterMatch" />

          <div class="flex flex-col gap-8">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">First Name <span class="text-error">*</span></label>
                <FosInput v-model="firstName" placeholder="First name" />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Last Name</label>
                <FosInput v-model="lastName" placeholder="Last name" />
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[10px] font-label uppercase tracking-widest text-outline">Phone <span class="text-error">*</span></label>
              <FosInput v-model="phone" type="tel" placeholder="(555) 000-0000" />
            </div>
            <!-- SAR field qualifications — not required for agency-less guests -->
            <template v-if="resolvedPath !== 'guest'">
              <div class="h-px bg-outline-variant/15" />
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Medical Qualification <span class="text-error">*</span></label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in medicalQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="medicalQual" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Searcher Type <span class="text-error">*</span></label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in searcherTypeOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="searcherType" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Rope Qualification <span class="text-error">*</span></label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in ropeQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="ropeQual" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>
              <div class="h-px bg-outline-variant/15" />
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Other Qualifications</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <label v-for="opt in miscQualOptions" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="checkbox" :value="opt" :checked="miscQuals.includes(opt)" class="accent-primary" @change="toggleMiscQual(opt)" />
                    {{ opt }}
                  </label>
                </div>
              </div>
            </template>
          </div>

          <div class="flex gap-3">
            <FosButton variant="secondary" @click="state = 'search'; resolvedPath = null; resolvedAgencyId = ''; resolvedAgencyName = ''; rosterMatch = null">
              Back
            </FosButton>
            <FosButton variant="primary" class="flex-1" :disabled="!canSubmit" @click="handleSubmit">
              {{ resolvedPath === 'new' ? 'Create Agency & Complete Setup' : resolvedPath === 'guest' ? 'Continue' : 'Submit Request' }}
            </FosButton>
          </div>
        </template>

      </div>
      </div>
    </div>
  </div>
</template>
